'use client';

import { Button, type ButtonProps } from '@govinity/ui/components/ui/button';
import { LoadingSpinner } from '@govinity/ui/components/ui/loading-spinner';
import { useToast } from '@govinity/ui/hooks/use-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BanIcon, RssIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';
import api from '@/lib/user-api';
import { useAuthStore } from '@/stores/authStore';
import { useGovernmentStore } from '@/stores/governmentStore';

type FollowGovernmentButtonProps = {
  className?: string;
  hideUnfollow?: boolean;
  size?: ButtonProps['size'];
};
export const FollowGovernmentButton = ({
  className,
  hideUnfollow = false,
  size = 'sm',
}: FollowGovernmentButtonProps) => {
  const t = useTranslations('FollowGovernmentButton');
  const government = useGovernmentStore((state) => state.government);
  const user = useAuthStore((state) => state.user);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const {
    data: followedGovernments,
    isLoading,
    error,
  } = useQuery({
    enabled: !!user && !!government,
    queryKey: ['governmentFollower', user?.id],
    queryFn: () => api.governmentFollowers.getAll(),
    staleTime: 24 * 60 * 60 * 1000, // 24 hours in milliseconds
    gcTime: 24 * 60 * 60 * 1000, // 24 hours in milliseconds
    refetchOnMount: false,
  });

  const { mutate: followGovernment, isPending } = useMutation({
    mutationFn: () => api.governmentFollowers.follow(government!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['governmentFollower', user?.id] });
      toast({
        title: t('followSuccess'),
        variant: 'success',
      });
    },
    onError: (err) => {
      toast({
        title: t('followError'),
        description: err instanceof Error ? err.message : t('unknownError'),
        variant: 'destructive',
      });
    },
  });

  const { mutate: unfollowGovernment, isPending: isPendingUnfollow } = useMutation({
    mutationFn: () => api.governmentFollowers.unfollow(government!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['governmentFollower', user?.id] });
      toast({
        title: t('unfollowSuccess'),
        variant: 'success',
      });
    },
    onError: (err) => {
      toast({
        title: t('unfollowError'),
        description: err instanceof Error ? err.message : t('unknownError'),
        variant: 'destructive',
      });
    },
  });

  if (!user || !government || error) {
    return null;
  }

  if (isLoading) {
    return (
      <div>
        <LoadingSpinner size={40} />
      </div>
    );
  }

  const isFollowed = followedGovernments?.some((gov) => gov.id === government.id) ?? false;

  if (isFollowed) {
    if (hideUnfollow) {
      return null;
    }
    return (
      <Button
        aria-label={t('unfollowAriaLabel')}
        className={className}
        onClick={() => unfollowGovernment()}
        disabled={isPendingUnfollow}
        size={size}
        variant="outline"
      >
        <BanIcon />
        {t('unfollow')}
      </Button>
    );
  }

  return (
    <Button
      aria-label={t('followAriaLabel')}
      className={className}
      onClick={() => followGovernment()}
      disabled={isPending}
      size={size}
      variant="primary-outline"
    >
      <RssIcon />
      {t('follow')}
    </Button>
  );
};
